import { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
}

interface Props {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder: string;
}

export function MultiSelectDropdown({ options, selected, onChange, placeholder }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="input w-full sm:max-w-[200px] text-left flex items-center gap-1 min-h-[38px] cursor-pointer"
      >
        {selected.length === 0 ? (
          <span className="text-spi-slate/50">{placeholder}</span>
        ) : (
          <span className="flex flex-wrap gap-1 text-xs">
            {selected.slice(0, 2).map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-0.5 bg-spi-green/10 text-spi-green-dark px-1.5 py-0.5 rounded"
              >
                {options.find((o) => o.value === v)?.label ?? v}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); toggle(v); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); toggle(v); } }}
                  className="ml-0.5 hover:text-red-600 cursor-pointer"
                >
                  &times;
                </span>
              </span>
            ))}
            {selected.length > 2 && (
              <span className="text-spi-slate text-xs">+{selected.length - 2}</span>
            )}
          </span>
        )}
        <svg className="w-4 h-4 ml-auto shrink-0 text-spi-slate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-56 bg-white border border-spi-parchment rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-2 hover:bg-spi-seed cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                className="rounded border-spi-parchment text-spi-green focus:ring-spi-green"
              />
              {opt.label}
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full px-3 py-2 text-xs text-spi-slate hover:text-red-600 border-t border-spi-parchment text-center"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}
